// src/config/dashboardCards.js
//
// Dashboard configuration — single source of truth for what appears on
// the dashboard and what permission each item requires.
//
// PULSE_STATS: top banner items, each gated on a single permission (or
// `requires: null` for items always shown like the date).
//
// CARDS: 3-column grid items, each a separate component file under
// src/components/dashboard/. Required permissions match each card's
// underlying data dependency.
//
// To add a new pulse stat or card:
//   1. (For cards) Build the component under src/components/dashboard/.
//   2. Add an entry to the appropriate array.
//   3. The Dashboard render loop picks it up automatically.
//
// Mirrors the architecture of src/config/matrix.js — config drives render.

import { PERMISSIONS } from "./permissions";
import { dashboardService } from "../services/dashboardService";
import { TimeRecordsCard } from "../components/dashboard/TimeRecordsCard";
import { WorkforceCard } from "../components/dashboard/WorkforceCard";
import { LastPayrollCard } from "../components/dashboard/LastPayrollCard";
import { OtApprovalCard } from "../components/dashboard/OtApprovalCard";
import { PayrollApprovalCard } from "../components/dashboard/PayrollApprovalCard";
import { AdjustmentsCard } from "../components/dashboard/AdjustmentsCard";
import { ActiveEmployeesCard } from "../components/dashboard/ActiveEmployeesCard";
import { BillingEstimateCard } from "../components/dashboard/BillingEstimateCard";

/**
 * Pulse stats — items rendered in the top "Today's Pulse" banner.
 *
 * Shape:
 *   {
 *     id:          string                                       // unique
 *     requires:    string | null                                // permission key, or null = always shown
 *     getValue:    (props) => string | number                   // displayed value
 *     getLabel?:   (props) => string                            // dynamic label (overrides static `label`)
 *     label?:      string                                       // static label
 *     valueClass?: string                                       // optional CSS class on the value element
 *   }
 *
 * Props passed in: { today, isLoading, stats, estimatedGross }
 */
export const DASHBOARD_PULSE_STATS = [
  {
    id: "date",
    requires: null,
    getValue: ({ today }) => dashboardService.formatShortDate(today),
    getLabel: ({ today }) => dashboardService.getDayOfWeek(today),
  },
  {
    id: "active",
    requires: PERMISSIONS.EMPLOYEES_VIEW, // 'emp:view'
    getValue: ({ isLoading, stats }) =>
      isLoading ? "..." : `${stats.active}/${stats.total}`,
    label: "Active",
  },
  {
    id: "on-leave",
    requires: PERMISSIONS.EMPLOYEES_VIEW,
    getValue: ({ isLoading, stats }) =>
      isLoading ? "..." : stats.onLeave,
    label: "On Leave",
  },
  {
    id: "hours-period",
    requires: PERMISSIONS.TIME_TRACKING_VIEW, // 'time:view'
    getValue: ({ isLoading }) => (isLoading ? "..." : "0"),
    label: "Hours (Period)",
  },
  {
    id: "est-gross",
    requires: PERMISSIONS.PAYROLL_VIEW, // 'payroll:view'
    getValue: ({ isLoading, estimatedGross }) =>
      isLoading
        ? "..."
        : dashboardService.formatCurrency(estimatedGross, true),
    label: "Est. Gross",
    valueClass: "pulse-stat__value--gold",
  },
];

/**
 * Cards — items rendered in the 3-column grid below the pulse banner.
 *
 * Shape:
 *   {
 *     id:        string         // unique
 *     requires:  string         // permission key required to render
 *     Component: React.FC<...>  // card component (file under src/components/dashboard/)
 *   }
 *
 * Each card receives the same props bag — the Dashboard component spreads
 * its derived data (isLoading, today, timeRecordsStats, activeEmployees,
 * departmentBreakdown, donutGradient, lastPayroll, otSummary,
 * canProcessPayroll, navigate, company) into every card. Cards read what
 * they need.
 */
export const DASHBOARD_CARDS = [
  {
    id: "time-records",
    requires: PERMISSIONS.TIME_TRACKING_VIEW,
    Component: TimeRecordsCard,
  },
  {
    id: "workforce",
    requires: PERMISSIONS.EMPLOYEES_VIEW,
    Component: WorkforceCard,
  },
  {
    id: "last-payroll",
    requires: PERMISSIONS.PAYROLL_VIEW, // 'payroll:view'
    Component: LastPayrollCard,
  },
  {
    id: "ot-approval",
    requires: PERMISSIONS.TIME_OT_VIEW, // 'time:ot:view' — read-only summary visible to view-level users
    Component: OtApprovalCard,
  },
  {
    id: "payroll-approval",
    requires: PERMISSIONS.PAYROLL_APPROVE, // 'payroll:approve' — only approvers see the queue
    Component: PayrollApprovalCard,
  },
  {
    id: "active-employees",
    requires: PERMISSIONS.SETTINGS_PAYMENTS_VIEW, // 'set:bill:view' — billing-capable only
    Component: ActiveEmployeesCard,
  },
  {
    id: "billing-estimate",
    requires: PERMISSIONS.SETTINGS_PAYMENTS_VIEW, // 'set:bill:view'
    Component: BillingEstimateCard,
  },
  {
    id: "adjustments",
    // 'adj:view' — matches the adjustments READ rule (firestore.rules: adj:view ||
    // payroll:view), so the client gate is a strict subset of what the rules grant and
    // no role can see this card and then hit permission-denied. Note the Adjustments
    // PAGE itself is broader (adj:edit OR adj:view, App.js), so an edit-only custom
    // role would reach the page without seeing the card — fail-safe, and no seeded role
    // has edit without view.
    requires: PERMISSIONS.ADJUSTMENTS_VIEW,
    Component: AdjustmentsCard,
  },
];